// src/components/admin/PromoCodeAdmin.tsx
import React, { useState } from 'react';
import { Shield, Tag, BarChart2, PlusCircle, List, Users } from 'lucide-react';
import PromoCodeCreationForm from './PromoCodeCreationForm';
import PromoCodeList from './PromoCodeList';
import PromoCodeDetail from './PromoCodeDetail';
import BatchCodeGenerator from './BatchCodeGenerator';
import PromoCodeAnalytics from './PromoCodeAnalytics';
import UserStatistics from './UserStatistics';
import { PromoCode } from '../../types/admin';

// Main admin interface component for promo code management
const PromoCodeAdmin: React.FC = () => {
  // Top-level: promo codes vs user statistics
  const [mainSection, setMainSection] = useState<'promo' | 'stats'>('promo');
  // Current active section of the admin interface (when in promo)
  const [activeSection, setActiveSection] = useState<
    'list' | 'create' | 'batch' | 'analytics' | 'detail'
  >('list');
  
  // Selected promo code for detail view
  const [selectedPromoCode, setSelectedPromoCode] = useState<PromoCode | null>(null);
  
  // Track loading state for async operations
  const [isLoading, setIsLoading] = useState(false);
  
  // Handle section changes
  const handleSectionChange = (section: 'list' | 'create' | 'batch' | 'analytics' | 'detail') => {
    setActiveSection(section);
    // Clear selected promo code when not in detail view
    if (section !== 'detail') {
      setSelectedPromoCode(null);
    }
  };
  
  // Handle viewing details of a specific promo code
  const handleViewPromoCode = (promoCode: PromoCode) => {
    setSelectedPromoCode(promoCode);
    setActiveSection('detail');
  };
  
  // Handle creation success
  const handleCreationSuccess = () => {
    setActiveSection('list');
    // Could add a success toast/notification here
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin header */}
      <header className="bg-gray-800 text-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center">
            <Shield className="h-8 w-8 mr-3" />
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          </div>
          <div>
            <span className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium">
              {mainSection === 'stats' ? 'User Statistics' : 'Promo Code Management'}
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Main navigation: Promo Codes vs User Statistics */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex -mb-px">
            <button
              onClick={() => setMainSection('promo')}
              className={`px-4 py-3 font-medium text-sm flex items-center ${
                mainSection === 'promo'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Tag className="mr-2 h-5 w-5" />
              Promo Codes
            </button>
            <button
              onClick={() => setMainSection('stats')}
              className={`px-4 py-3 font-medium text-sm flex items-center ${
                mainSection === 'stats'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="mr-2 h-5 w-5" />
              User Statistics
            </button>
          </nav>
        </div>

        {mainSection === 'stats' ? (
          <div className="bg-white shadow rounded-lg p-6">
            <UserStatistics />
          </div>
        ) : (
          <>
        {/* Promo code sub-navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex -mb-px">
            <button
              onClick={() => handleSectionChange('list')}
              className={`px-4 py-3 font-medium text-sm flex items-center ${
                activeSection === 'list'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <List className="mr-2 h-5 w-5" />
              Manage Promo Codes
            </button>
            <button
              onClick={() => handleSectionChange('create')}
              className={`px-4 py-3 font-medium text-sm flex items-center ${
                activeSection === 'create'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <PlusCircle className="mr-2 h-5 w-5" />
              Create Code
            </button>
            <button
              onClick={() => handleSectionChange('batch')}
              className={`px-4 py-3 font-medium text-sm flex items-center ${
                activeSection === 'batch'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Tag className="mr-2 h-5 w-5" />
              Batch Generate
            </button>
            <button
              onClick={() => handleSectionChange('analytics')}
              className={`px-4 py-3 font-medium text-sm flex items-center ${
                activeSection === 'analytics'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart2 className="mr-2 h-5 w-5" />
              Analytics
            </button>
          </nav>
        </div>

        {/* Content area */}
        <div className="bg-white shadow rounded-lg p-6">
          {activeSection === 'list' && (
            <PromoCodeList 
              onViewPromoCode={handleViewPromoCode}
              onCreateNew={() => handleSectionChange('create')}
            />
          )}
          
          {activeSection === 'create' && (
            <PromoCodeCreationForm 
              onSuccess={handleCreationSuccess}
              onCancel={() => handleSectionChange('list')}
            />
          )}
          
          {activeSection === 'batch' && (
            <BatchCodeGenerator 
              onSuccess={() => handleSectionChange('list')}
              onCancel={() => handleSectionChange('list')}
            />
          )}
          
          {activeSection === 'analytics' && (
            <PromoCodeAnalytics />
          )}
          
          {activeSection === 'detail' && selectedPromoCode && (
            <PromoCodeDetail 
              promoCode={selectedPromoCode}
              onBack={() => handleSectionChange('list')}
              onUpdate={(updatedCode) => {
                setSelectedPromoCode(updatedCode);
                // Could refresh the list if needed
              }}
            />
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PromoCodeAdmin;